import { fetchJson } from '@flos/react-ui';
import { addMonths, format } from 'date-fns';
import { createContext, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { defaultQueue } from '../const';
import { SettingsState } from '../store/settings.type';
import { AvailabilityInfo, IState } from '../types';
import { ISO_DATE_FORMAT } from '../utils/date-utils';
import log from '../utils/logger';

const startDate = new Date();
const endDate = addMonths(startDate, 3);
const AvailabilitiesContext = createContext<{
  startDate: Date;
  endDate: Date;
  refresh: () => Promise<void>;
  availabilities: AvailabilityInfo;
}>({
  startDate,
  endDate,
  refresh: () => Promise.resolve(),
  availabilities: {},
});

let testDnis: string | undefined;

switch (window.location.hash) {
  case '#high':
    testDnis = 'TEST_HIGH';
    break;
  case '#low':
    testDnis = 'TEST_LOW';
    break;
  case '#test':
    testDnis = 'Telefoni_Test_WCB';
    break;
  default:
    testDnis = undefined;
}

const AvailabilityContext = ({ children }: { children: React.ReactNode }) => {
  const [availabilities, setAvailabilities] = useState<AvailabilityInfo>({});
  const {
    appName,
    endpointConfig: { apiAwsBase: host },
    siteConfig: { env, environment },
  } = useSelector<IState, SettingsState>((state) => state.settings);

  const fetchAvailabilities = async () => {
    try {
      const response = await fetchJson(`${host}/ninjas-schedule-a-callback-api-v2/v1/availabilities`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-top-appname': appName,
          'x-top-host': `wso2${env === 'prod' ? 'prod' : 'reltest'}.topdanmark.local`,
          'x-top-env': environment,
        },
        body: JSON.stringify({
          startDate: format(startDate, ISO_DATE_FORMAT),
          endDate: format(endDate, ISO_DATE_FORMAT),
          department: defaultQueue,
          ...(!!testDnis ? { dnis: testDnis } : {}),
        }),
      });

      const availabilityResponse = await response;
      setAvailabilities(availabilityResponse);
    } catch (e) {
      log.error('Error fetching availabilities', e);
    }
  };

  useEffect(() => {
    fetchAvailabilities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AvailabilitiesContext.Provider
      value={{
        startDate,
        endDate,
        refresh: fetchAvailabilities,
        availabilities,
      }}
    >
      {children}
    </AvailabilitiesContext.Provider>
  );
};

export default AvailabilityContext;

export const useAvailabilities = () => useContext(AvailabilitiesContext);
